/**
 * ============================================
 * SERVICE DE OCR - VERSÃO 2.0 OTIMIZADA
 * ============================================
 * 
 * Utiliza Tesseract.js com pré-processamento avançado de imagem
 * para melhorar significativamente o reconhecimento de manuscritos
 * 
 * OTIMIZAÇÕES APLICADAS:
 * - Aumento de resolução (2x)
 * - Conversão para escala de cinza
 * - Aumento de contraste (1.5x)
 * - Binarização adaptativa (Otsu's method)
 * - Remoção de ruído
 * - Whitelist de caracteres portugueses
 * 
 * @author Sistema de Gestão de Consultório de Psicologia
 * @date 2025-11-22
 * @version 2.0.0
 */

import Tesseract, { createWorker } from 'tesseract.js';

interface ResultadoOCR {
  texto: string;
  confianca: number;
  totalCaracteres: number;
  tempoProcessamento: number;
}

interface ProgressoOCR {
  status: 'carregando' | 'reconhecendo' | 'concluido' | 'preprocessando';
  progresso: number;
  mensagem: string;
}

// ========================================
// DICAS PARA MELHORAR QUALIDADE DO OCR
// ========================================
export const DICAS_OCR_MANUSCRITOS = [
  '📸 Tire foto com boa iluminação natural (evite flash)',
  '📐 Mantenha a câmera perpendicular ao papel (sem ângulo)',
  '🔍 Certifique-se de que o texto está nítido e legível',
  '📏 Enquadre apenas o texto a ser digitalizado',
  '✍️ LETRA DE FORMA (CAIXA ALTA) funciona MUITO melhor que cursiva',
  '🖼️ Evite sombras, reflexos e fundos coloridos',
  '⚡ Imagens em alta resolução têm melhor resultado',
  '📝 Texto preto em papel branco tem melhor reconhecimento'
];

// ========================================
// Pré-processar imagem para melhorar OCR
// ========================================
async function preprocessarImagem(
  arquivo: File,
  onProgress?: (progresso: ProgressoOCR) => void
): Promise<string> {
  console.log('[OCR-PREPROCESSAMENTO] Iniciando otimização de imagem...');

  if (onProgress) {
    onProgress({
      status: 'preprocessando',
      progresso: 5,
      mensagem: 'Carregando imagem...'
    });
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      try {
        console.log('[OCR-PREPROCESSAMENTO] Imagem carregada. Dimensões:', img.width, 'x', img.height);

        // Criar canvas para processamento
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

        // ETAPA 1: Aumentar resolução (2x para melhorar OCR)
        const scale = 2;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        if (onProgress) {
          onProgress({
            status: 'preprocessando',
            progresso: 10,
            mensagem: 'Aumentando resolução (2x)...'
          });
        }

        // Desenhar imagem em alta qualidade
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Obter dados da imagem
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        console.log('[OCR-PREPROCESSAMENTO] Imagem escalada. Nova resolução:', canvas.width, 'x', canvas.height);

        // ETAPA 2: Converter para escala de cinza
        if (onProgress) {
          onProgress({
            status: 'preprocessando',
            progresso: 20,
            mensagem: 'Convertendo para escala de cinza...'
          });
        }

        for (let i = 0; i < data.length; i += 4) {
          const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
          data[i] = gray;
          data[i + 1] = gray;
          data[i + 2] = gray;
        }

        console.log('[OCR-PREPROCESSAMENTO] Conversão para cinza concluída');

        // ETAPA 3: Aumentar contraste (crítico para manuscritos)
        if (onProgress) {
          onProgress({
            status: 'preprocessando',
            progresso: 30,
            mensagem: 'Aumentando contraste (1.5x)...'
          });
        }

        const contrast = 1.5;
        const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
        
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.min(255, Math.max(0, factor * (data[i] - 128) + 128));
          data[i + 1] = data[i];
          data[i + 2] = data[i];
        }

        console.log('[OCR-PREPROCESSAMENTO] Contraste aumentado');

        // ETAPA 4: Binarização adaptativa (Otsu's method)
        if (onProgress) {
          onProgress({
            status: 'preprocessando',
            progresso: 50,
            mensagem: 'Aplicando binarização adaptativa...'
          });
        }

        // Calcular histograma
        const histogram = new Array(256).fill(0);
        for (let i = 0; i < data.length; i += 4) {
          histogram[Math.floor(data[i])]++;
        }

        // Calcular threshold ótimo (Otsu's method)
        let sum = 0;
        for (let i = 0; i < 256; i++) {
          sum += i * histogram[i];
        }

        let sumB = 0;
        let wB = 0;
        let wF = 0;
        let maxVariance = 0;
        let threshold = 0;
        const total = canvas.width * canvas.height;

        for (let i = 0; i < 256; i++) {
          wB += histogram[i];
          if (wB === 0) continue;

          wF = total - wB;
          if (wF === 0) break;

          sumB += i * histogram[i];
          const mB = sumB / wB;
          const mF = (sum - sumB) / wF;

          const variance = wB * wF * (mB - mF) * (mB - mF);

          if (variance > maxVariance) {
            maxVariance = variance;
            threshold = i;
          }
        }

        console.log('[OCR-PREPROCESSAMENTO] Threshold calculado:', threshold);

        // Aplicar threshold
        for (let i = 0; i < data.length; i += 4) {
          const value = data[i] > threshold ? 255 : 0;
          data[i] = value;
          data[i + 1] = value;
          data[i + 2] = value;
        }

        console.log('[OCR-PREPROCESSAMENTO] Binarização concluída');

        // ETAPA 5: Remover ruído (remover pixels isolados)
        if (onProgress) {
          onProgress({
            status: 'preprocessando',
            progresso: 70,
            mensagem: 'Removendo ruído...'
          });
        }

        const tempData = new Uint8ClampedArray(data);
        for (let y = 1; y < canvas.height - 1; y++) {
          for (let x = 1; x < canvas.width - 1; x++) {
            const idx = (y * canvas.width + x) * 4;
            
            // Contar vizinhos escuros (texto)
            let darkNeighbors = 0;
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                const nIdx = ((y + dy) * canvas.width + (x + dx)) * 4;
                if (tempData[nIdx] === 0) darkNeighbors++;
              }
            }

            // Se pixel escuro está isolado, remover (é ruído)
            if (tempData[idx] === 0 && darkNeighbors < 2) {
              data[idx] = 255;
              data[idx + 1] = 255;
              data[idx + 2] = 255;
            }
          }
        }

        console.log('[OCR-PREPROCESSAMENTO] Remoção de ruído concluída');

        // Aplicar dados processados
        ctx.putImageData(imageData, 0, 0);

        if (onProgress) {
          onProgress({
            status: 'preprocessando',
            progresso: 80,
            mensagem: 'Finalizando otimização...'
          });
        }

        // Converter para base64
        const dataUrl = canvas.toDataURL('image/png');
        console.log('[OCR-PREPROCESSAMENTO] ✅ Concluído com sucesso!');
        
        resolve(dataUrl);
      } catch (error) {
        console.error('[OCR-PREPROCESSAMENTO] ❌ Erro:', error);
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Erro ao carregar imagem'));
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    reader.readAsDataURL(arquivo);
  });
}

// ========================================
// Processar imagem com OCR no browser
// ========================================
export async function processarImagemOCR(
  arquivo: File,
  onProgress?: (progresso: ProgressoOCR) => void
): Promise<ResultadoOCR> {
  console.log('[OCR-CLIENTE] 🚀 Iniciando processamento de imagem:', arquivo.name);
  
  const inicio = Date.now();

  try {
    // ETAPA 1: PRÉ-PROCESSAR IMAGEM (otimizações para manuscritos)
    const imagemProcessada = await preprocessarImagem(arquivo, onProgress);

    if (onProgress) {
      onProgress({
        status: 'carregando',
        progresso: 85,
        mensagem: 'Carregando engine OCR...'
      });
    }

    // ETAPA 2: Criar worker Tesseract com configurações otimizadas
    const worker = await createWorker('por', 1, {
      logger: (m) => {
        if (m.status === 'loading tesseract core' || m.status === 'initializing tesseract') {
          console.log('[TESSERACT]', m.status, `${Math.round(m.progress * 100)}%`);
          
          if (onProgress) {
            onProgress({
              status: 'carregando',
              progresso: 85 + (m.progress * 5), // 85-90%
              mensagem: 'Inicializando Tesseract...'
            });
          }
        } else if (m.status === 'recognizing text') {
          console.log('[TESSERACT] Reconhecendo texto:', `${Math.round(m.progress * 100)}%`);
          
          if (onProgress) {
            onProgress({
              status: 'reconhecendo',
              progresso: 90 + (m.progress * 10), // 90-100%
              mensagem: `Reconhecendo texto manuscrito: ${Math.round(m.progress * 100)}%`
            });
          }
        }
      },
    });

    // ETAPA 3: CONFIGURAÇÕES OTIMIZADAS PARA MANUSCRITOS
    await worker.setParameters({
      tessedit_pageseg_mode: Tesseract.PSM.AUTO, // Auto detectar layout
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzÀÁÂÃÇÉÊÍÓÔÕÚàáâãçéêíóôõú0123456789.,;:!?()\n -', // Caracteres PT-BR
    });

    console.log('[OCR-CLIENTE] Reconhecendo texto da imagem otimizada...');

    // ETAPA 4: Reconhecer texto
    const { data } = await worker.recognize(imagemProcessada);
    
    await worker.terminate();

    const tempoProcessamento = Date.now() - inicio;

    console.log('[OCR-CLIENTE] ✅ Processamento concluído:', {
      caracteres: data.text.length,
      confianca: data.confidence.toFixed(2) + '%',
      tempo: (tempoProcessamento / 1000).toFixed(2) + 's'
    });

    if (onProgress) {
      onProgress({
        status: 'concluido',
        progresso: 100,
        mensagem: `Texto extraído! Confiança: ${data.confidence.toFixed(1)}%`
      });
    }

    // Limpar texto extraído
    let textoLimpo = data.text
      .trim()
      .replace(/\n{3,}/g, '\n\n') // Máximo 2 quebras de linha
      .replace(/[ \t]{2,}/g, ' ') // Máximo 1 espaço
      .replace(/\r/g, ''); // Remover carriage return

    // Alertar se confiança está baixa
    if (data.confidence < 50) {
      console.warn('[OCR-CLIENTE] ⚠️ Confiança baixa! Sugerido: melhorar qualidade da imagem');
    }

    return {
      texto: textoLimpo,
      confianca: data.confidence,
      totalCaracteres: textoLimpo.length,
      tempoProcessamento
    };

  } catch (error: any) {
    console.error('[OCR-CLIENTE] ❌ Erro ao processar imagem:', error);
    throw new Error(`Erro no OCR: ${error.message}`);
  }
}

// ========================================
// Validar arquivo antes de processar
// ========================================
export function validarArquivoOCR(arquivo: File): { valido: boolean; erro?: string } {
  const tiposPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/bmp', 'image/tiff'];
  const tamanhoMaximo = 10 * 1024 * 1024; // 10 MB

  if (!tiposPermitidos.includes(arquivo.type)) {
    return {
      valido: false,
      erro: 'Tipo de arquivo não suportado. Use JPG, PNG, BMP ou TIFF.'
    };
  }

  if (arquivo.size > tamanhoMaximo) {
    return {
      valido: false,
      erro: 'Arquivo muito grande. Tamanho máximo: 10 MB.'
    };
  }

  if (arquivo.size < 1024) {
    return {
      valido: false,
      erro: 'Arquivo muito pequeno. Verifique se é uma imagem válida.'
    };
  }

  return { valido: true };
}

// ========================================
// Pré-processar imagem para melhorar OCR de manuscritos
// ========================================
export async function prepararImagemParaOCR(arquivo: File): Promise<string> {
  return await preprocessarImagem(arquivo);
}
